import React, { useState, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export interface MetricData {
  timestamp: number;
  value: number;
}

export interface SystemMetrics {
  responseTime: MetricData[];
  throughput: MetricData[];
  errorRate: MetricData[];
  cpuUsage: MetricData[];
  memoryUsage: MetricData[];
  activeConnections: MetricData[];
}

export interface AlertRule {
  metric: string;
  threshold: number;
  operator: 'gt' | 'lt' | 'eq';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
}

export interface Alert {
  id: string;
  rule: AlertRule;
  value: number;
  timestamp: number;
  acknowledged: boolean;
}

export interface PerformanceDashboardProps {
  /** Real-time metrics data */
  metrics?: SystemMetrics;
  /** Active alerts */
  alerts?: Alert[];
  /** WebSocket connection status */
  connectionStatus?: 'connected' | 'connecting' | 'disconnected' | 'error';
  /** Callback when alert is acknowledged */
  onAcknowledgeAlert?: (alertId: string) => void;
  /** Callback when metric threshold is changed */
  onThresholdChange?: (metric: string, threshold: number) => void;
  /** Whether to show real-time updates */
  realTimeUpdates?: boolean;
  /** Custom className for styling */
  className?: string;
  /** Theme variant */
  variant?: 'light' | 'dark';
  /** Time range for metrics display */
  timeRange?: '5m' | '15m' | '1h' | '6h' | '24h';
}

const METRIC_CONFIGS = {
  responseTime: {
    label: 'Response Time',
    unit: 'ms',
    color: '#3B82F6',
    threshold: 500,
    criticalThreshold: 1000
  },
  throughput: {
    label: 'Throughput',
    unit: 'req/s',
    color: '#10B981',
    threshold: 100,
    criticalThreshold: 50
  },
  errorRate: {
    label: 'Error Rate',
    unit: '%',
    color: '#EF4444',
    threshold: 1,
    criticalThreshold: 5
  },
  cpuUsage: {
    label: 'CPU Usage',
    unit: '%',
    color: '#F59E0B',
    threshold: 70,
    criticalThreshold: 90
  },
  memoryUsage: {
    label: 'Memory Usage',
    unit: '%',
    color: '#8B5CF6',
    threshold: 80,
    criticalThreshold: 95
  },
  activeConnections: {
    label: 'Active Connections',
    unit: '',
    color: '#06B6D4',
    threshold: 1000,
    criticalThreshold: 5000
  }
};

const generateMockData = (points: number = 50): SystemMetrics => {
  const now = Date.now();
  const interval = 5000; // 5 seconds

  const generatePoints = (baseValue: number, variance: number): MetricData[] => {
    return Array.from({ length: points }, (_, i) => ({
      timestamp: now - (points - i - 1) * interval,
      value: Math.max(0, baseValue + (Math.random() - 0.5) * variance)
    }));
  };

  return {
    responseTime: generatePoints(200, 150),
    throughput: generatePoints(120, 40),
    errorRate: generatePoints(0.5, 1),
    cpuUsage: generatePoints(45, 30),
    memoryUsage: generatePoints(60, 25),
    activeConnections: generatePoints(500, 200)
  };
};

export const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({
  metrics: initialMetrics,
  alerts = [],
  connectionStatus = 'connected',
  onAcknowledgeAlert,
  onThresholdChange,
  realTimeUpdates = true,
  className,
  variant = 'light',
  timeRange = '15m'
}) => {
  const [metrics, setMetrics] = useState<SystemMetrics>(initialMetrics || generateMockData());
  const [selectedMetric, setSelectedMetric] = useState<keyof SystemMetrics>('responseTime');
  const [thresholds, setThresholds] = useState<Record<string, number>>(
    Object.fromEntries(
      Object.entries(METRIC_CONFIGS).map(([key, config]) => [key, config.threshold])
    )
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (realTimeUpdates && !initialMetrics) {
      // Simulate real-time updates
      intervalRef.current = setInterval(() => {
        setMetrics(prev => {
          const newMetrics = { ...prev };
          const now = Date.now();

          Object.keys(newMetrics).forEach(metricKey => {
            const metric = newMetrics[metricKey as keyof SystemMetrics];
            const lastValue = metric[metric.length - 1]?.value || 0;

            // Generate new data point
            const newValue = Math.max(0, lastValue + (Math.random() - 0.5) * 20);

            // Keep only recent data points
            const maxPoints = 100;
            if (metric.length >= maxPoints) {
              metric.shift();
            }

            metric.push({
              timestamp: now,
              value: newValue
            });
          });

          return newMetrics;
        });
      }, 2000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [realTimeUpdates, initialMetrics]);

  const formatValue = (value: number, unit: string): string => {
    if (unit === '%') {
      return `${value.toFixed(1)}%`;
    }
    if (unit === 'ms') {
      return `${value.toFixed(0)}ms`;
    }
    if (unit === 'req/s') {
      return `${value.toFixed(1)} req/s`;
    }
    return value.toFixed(0);
  };

  const getMetricStatus = (metricKey: string, value: number): 'normal' | 'warning' | 'critical' => {
    const config = METRIC_CONFIGS[metricKey as keyof typeof METRIC_CONFIGS];
    if (value >= config.criticalThreshold) return 'critical';
    if (value >= config.threshold) return 'warning';
    return 'normal';
  };

  const createChartData = (metricKey: keyof SystemMetrics) => {
    const data = metrics[metricKey];
    const config = METRIC_CONFIGS[metricKey];

    return {
      labels: data.map(point =>
        new Date(point.timestamp).toLocaleTimeString('en-US', {
          hour12: false,
          minute: '2-digit',
          second: '2-digit'
        })
      ),
      datasets: [
        {
          label: config.label,
          data: data.map(point => point.value),
          borderColor: config.color,
          backgroundColor: `${config.color}20`,
          borderWidth: 2,
          fill: true,
          tension: 0.1,
          pointRadius: 0,
          pointHoverRadius: 4
        },
        {
          label: 'Threshold',
          data: data.map(() => thresholds[metricKey]),
          borderColor: '#EF4444',
          backgroundColor: 'transparent',
          borderWidth: 1,
          borderDash: [5, 5],
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: variant === 'dark' ? '#E5E7EB' : '#374151'
        }
      },
      tooltip: {
        backgroundColor: variant === 'dark' ? '#1F2937' : '#FFFFFF',
        titleColor: variant === 'dark' ? '#E5E7EB' : '#374151',
        bodyColor: variant === 'dark' ? '#E5E7EB' : '#374151',
        borderColor: variant === 'dark' ? '#374151' : '#E5E7EB',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        grid: {
          color: variant === 'dark' ? '#374151' : '#E5E7EB'
        },
        ticks: {
          color: variant === 'dark' ? '#9CA3AF' : '#6B7280'
        }
      },
      y: {
        grid: {
          color: variant === 'dark' ? '#374151' : '#E5E7EB'
        },
        ticks: {
          color: variant === 'dark' ? '#9CA3AF' : '#6B7280'
        }
      }
    },
    elements: {
      point: {
        hoverBackgroundColor: variant === 'dark' ? '#1F2937' : '#FFFFFF'
      }
    }
  };

  const currentValues = Object.fromEntries(
    Object.entries(metrics).map(([key, data]) => [
      key,
      data[data.length - 1]?.value || 0
    ])
  );

  return (
    <div className={clsx(
      'w-full max-w-7xl mx-auto',
      variant === 'dark' ? 'text-white' : 'text-gray-900',
      className
    )}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold mb-2">Performance Dashboard</h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className={clsx(
                'w-2 h-2 rounded-full mr-2',
                connectionStatus === 'connected' ? 'bg-green-500' :
                connectionStatus === 'connecting' ? 'bg-yellow-500' :
                'bg-red-500'
              )}></div>
              <span className="text-sm capitalize">{connectionStatus}</span>
            </div>
            {realTimeUpdates && (
              <span className={clsx(
                'text-xs px-2 py-1 rounded-full',
                variant === 'dark' ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
              )}>
                Live
              </span>
            )}
          </div>
        </div>

        {/* Time Range Selector */}
        <select
          value={timeRange}
          onChange={() => {}} // Placeholder - would update time range
          className={clsx(
            'px-3 py-2 border rounded-md text-sm',
            'focus:outline-none focus:ring-2 focus:ring-blue-500',
            variant === 'dark'
              ? 'bg-gray-800 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          )}
        >
          <option value="5m">Last 5 minutes</option>
          <option value="15m">Last 15 minutes</option>
          <option value="1h">Last hour</option>
          <option value="6h">Last 6 hours</option>
          <option value="24h">Last 24 hours</option>
        </select>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">Active Alerts</h3>
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={clsx(
                  'flex items-center justify-between p-3 rounded-lg border',
                  alert.rule.severity === 'critical' ? 'bg-red-50 border-red-200' :
                  alert.rule.severity === 'high' ? 'bg-orange-50 border-orange-200' :
                  alert.rule.severity === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                  'bg-blue-50 border-blue-200'
                )}
              >
                <div>
                  <div className="font-medium text-gray-900">{alert.rule.message}</div>
                  <div className="text-sm text-gray-600">
                    {alert.rule.metric}: {alert.value} (threshold: {alert.rule.threshold})
                  </div>
                </div>
                {!alert.acknowledged && (
                  <button
                    onClick={() => onAcknowledgeAlert?.(alert.id)}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Acknowledge
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metrics Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {Object.entries(METRIC_CONFIGS).map(([key, config]) => {
          const value = currentValues[key];
          const status = getMetricStatus(key, value);

          return (
            <div
              key={key}
              className={clsx(
                'p-4 border rounded-lg cursor-pointer transition-all',
                variant === 'dark'
                  ? 'border-gray-700 hover:border-gray-600'
                  : 'border-gray-200 hover:border-gray-300',
                selectedMetric === key && (
                  variant === 'dark' ? 'bg-gray-800 border-blue-500' : 'bg-blue-50 border-blue-300'
                )
              )}
              onClick={() => setSelectedMetric(key as keyof SystemMetrics)}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium">{config.label}</h4>
                <div className={clsx(
                  'w-2 h-2 rounded-full',
                  status === 'critical' ? 'bg-red-500' :
                  status === 'warning' ? 'bg-yellow-500' :
                  'bg-green-500'
                )}></div>
              </div>
              <div className="text-2xl font-bold mb-1">
                {formatValue(value, config.unit)}
              </div>
              <div className={clsx(
                'text-xs',
                variant === 'dark' ? 'text-gray-400' : 'text-gray-500'
              )}>
                Threshold: {formatValue(thresholds[key], config.unit)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Chart */}
      <div className={clsx(
        'border rounded-lg p-6',
        variant === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
      )}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {METRIC_CONFIGS[selectedMetric].label} Over Time
          </h3>

          <div className="flex items-center space-x-2">
            <label className="text-sm">Threshold:</label>
            <input
              type="number"
              value={thresholds[selectedMetric]}
              onChange={(e) => {
                const newThreshold = parseFloat(e.target.value);
                setThresholds(prev => ({ ...prev, [selectedMetric]: newThreshold }));
                onThresholdChange?.(selectedMetric, newThreshold);
              }}
              className={clsx(
                'w-20 px-2 py-1 border rounded text-sm',
                'focus:outline-none focus:ring-2 focus:ring-blue-500',
                variant === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              )}
            />
          </div>
        </div>

        <div className="h-64">
          <Line data={createChartData(selectedMetric)} options={chartOptions} />
        </div>
      </div>

      {/* System Status */}
      <div className={clsx(
        'mt-6 p-4 border rounded-lg',
        variant === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
      )}>
        <h3 className="font-semibold mb-3">System Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className={clsx(
              'font-medium',
              variant === 'dark' ? 'text-gray-300' : 'text-gray-600'
            )}>
              Uptime:
            </span>
            <span className="ml-2">99.9% (30d)</span>
          </div>
          <div>
            <span className={clsx(
              'font-medium',
              variant === 'dark' ? 'text-gray-300' : 'text-gray-600'
            )}>
              Last Updated:
            </span>
            <span className="ml-2">{new Date().toLocaleTimeString()}</span>
          </div>
          <div>
            <span className={clsx(
              'font-medium',
              variant === 'dark' ? 'text-gray-300' : 'text-gray-600'
            )}>
              Data Points:
            </span>
            <span className="ml-2">{metrics[selectedMetric].length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceDashboard;
